import React from 'react';
import styles from './ReportMarkedFigure.module.css';

const MARK_RADIUS = 9;
const STROKE_WIDTH = 2.5;
const ARROW_SIZE = 8;

/**
 * Figura (desenho ou foto recortada) com marcações numeradas, desenhada para o
 * PDF: o html2canvas transforma um SVG em imagem e, nesse processo, não
 * carrega `<image>` externas. Por isso o fundo é um `<img>` normal, os
 * números são círculos em HTML e só linhas e setas dos traços ficam num SVG
 * sobreposto, sem recursos externos.
 *
 * `src` e `frame` ({ width, height }): imagem de fundo e seu quadro.
 * `photo`: { width, height, crop } para uma foto do paciente recortada
 *   (tamanho natural em pixels e recorte em frações); sem `photo`, o quadro
 *   é o da imagem inteira.
 * `width`: largura da figura em pixels no papel.
 * `marks`: [{ key, x, y, x2, y2, kind ('point' | 'stroke'), color, number, label }],
 *   coordenadas em frações do quadro.
 */
const ReportMarkedFigure = ({ src, frame, photo, width, marks }) => {
  let aspect = frame ? frame.width / frame.height : 1;
  let imageStyle = { width: '100%', height: '100%' };

  if (photo && photo.width > 0 && photo.height > 0) {
    const crop = photo.crop || { x: 0, y: 0, w: 1, h: 1 };
    aspect = (crop.w * photo.width) / (crop.h * photo.height);
    const scaledWidth = width / crop.w;
    const scaledHeight = scaledWidth * (photo.height / photo.width);
    imageStyle = {
      width: `${scaledWidth}px`,
      height: `${scaledHeight}px`,
      left: `${-crop.x * scaledWidth}px`,
      top: `${-crop.y * scaledHeight}px`,
    };
  }

  const height = Math.round(width / aspect);
  const strokes = (marks || []).filter((mark) => mark.kind === 'stroke' && mark.x2 != null && mark.y2 != null);

  return (
    <div className={styles.figure} style={{ width: `${width}px`, height: `${height}px` }}>
      <img className={styles.image} src={src} alt="" style={imageStyle} />

      {strokes.length > 0 && (
        <svg className={styles.overlay} viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
          {strokes.map((mark) => {
            const x1 = mark.x * width;
            const y1 = mark.y * height;
            const x2 = mark.x2 * width;
            const y2 = mark.y2 * height;
            const angle = Math.atan2(y2 - y1, x2 - x1);
            const left = angle + Math.PI * 0.8;
            const right = angle - Math.PI * 0.8;
            const points = [
              `${x2},${y2}`,
              `${x2 + Math.cos(left) * ARROW_SIZE},${y2 + Math.sin(left) * ARROW_SIZE}`,
              `${x2 + Math.cos(right) * ARROW_SIZE},${y2 + Math.sin(right) * ARROW_SIZE}`,
            ].join(' ');
            return (
              <g key={mark.key}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={mark.color} strokeWidth={STROKE_WIDTH} strokeLinecap="round" />
                <polygon points={points} fill={mark.color} />
              </g>
            );
          })}
        </svg>
      )}

      {(marks || []).map((mark) => {
        const left = mark.x * width;
        const top = mark.y * height;
        return (
          <React.Fragment key={mark.key}>
            <span
              className={styles.mark}
              style={{
                left: `${left - MARK_RADIUS}px`,
                top: `${top - MARK_RADIUS}px`,
                width: `${MARK_RADIUS * 2}px`,
                height: `${MARK_RADIUS * 2}px`,
                lineHeight: `${MARK_RADIUS * 2}px`,
                background: mark.color,
              }}
            >
              {mark.number}
            </span>
            {mark.label && (
              <span className={styles.label} style={{ left: `${left + MARK_RADIUS + 3}px`, top: `${top - 8}px` }}>
                {mark.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ReportMarkedFigure;
